import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { SpecFilters } from '@deltek/specification-client/core/Widgets/SpecTable'
import { createDevStore } from 'bpm/shared/utils/devtools'
import debounce from 'lodash.debounce'
import { StateCreator, create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { defaultSortOrder } from 'bpm/pages/ProductCardManagementPage/ProductListingsPage/ProductListingsPage.constants'
import { columns, defaultColumns } from 'bpm/pages/TablePage/tablePage'
import { IdTitleProps, PinningProps } from './TableSettings'

export type TableSettingsState = {
    groupEnabled: boolean
    availableColumns: IdTitleProps[]
    selectedColumns: IdTitleProps[]
    groupColumns: IdTitleProps[]
    collapseColumnChecked: Record<string, boolean>
    orderBy: string
    filters: SpecFilters<ListingMatch>
    areFiltersVisible: boolean
    columnWidths?: Record<string, number>
    isTableSettingLoaded: boolean
    pinningProps: PinningProps
    lastUpdated: number
    isDataUploaded: boolean
    isTableView: boolean
    selectedRowIds: Record<string, boolean>
    setGroupEnabled: (
        groupEnabled: boolean,
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setAvailableColumns: (
        availableColumns: IdTitleProps[],
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setSelectedColumns: (
        selectedColumns: IdTitleProps[],
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setGroupColumns: (
        groupColumns: IdTitleProps[],
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setPinningProps: (
        pinningProps: PinningProps,
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setCollapseColumnChecked: (
        collapseColumnChecked: Record<string, boolean>,
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setOrderBy: (
        orderBy: string,
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setFilters: (filters: SpecFilters<ListingMatch>) => void
    setTableSettings: (
        tableSettings: Partial<TableSettingsState>,
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setColumnWidths: (
        columnWidth: Record<string, number>,
        saveTableViewSettings?: (state: TableSettingsState) => void
    ) => void
    setIsTableView: (isTableView: boolean) => void
    setIsTableSettingLoaded: (isLoaded: boolean) => void
    setIsDataUploaded: (isUploaded: boolean) => void
    setSelectedRowIds: (selectedRowIds: Record<string, boolean>) => void
}

const emptyListingMatch: Partial<ListingMatch> = {
    listingId: '',
    bimLinks: [],
    catalogs: [],
    categories: [],
    displayNameWithFirmName: '',
    firmDisplayName: '',
    firmId: '',
    firmLogoUrl: '',
    firmName: '',
    type: undefined,
    images: [],
    isActive: false,
    lastUpdated: '',
    manufacturerDocs: [],
    productId: '',
    productName: '',
    productType: '',
    sectionName: '',
    submissionStatus: '',
    videos: [],
    canAutoGenerateProductSpecification: false,
    specAgentProductTypeId: 1,
    hasProductDetailNotes: false,
    model: '',
}

export const DEFAULT_TABLE_FILTERS = Object.keys(emptyListingMatch).map(
    (key) => {
        return { id: key, value: '' }
    }
)

export const DEFAULT_TABLE_SETTINGS = {
    groupEnabled: true,
    availableColumns: columns.map((col) => ({
        id: `${col.id}`,
        title: `${col.Header}`,
    })),
    selectedColumns: defaultColumns.map((col) => ({
        id: `${col.id}`,
        title: `${col.Header}`,
    })),
    groupColumns: [
        { id: 'sectionName', title: 'Family Name' },
        { id: 'productType', title: 'Product Type' },
    ],
    pinningProps: {
        firstColumnPinned: false,
        lastColumnPinned: false,
    },
    collapseColumnChecked: {},
    orderBy: defaultSortOrder,
    filters: DEFAULT_TABLE_FILTERS,
    areFiltersVisible: false, // TODO: Implement this
    columnWidths: {},
    isTableSettingLoaded: false,
    lastUpdated: 0,
    isDataUploaded: true,
    isTableView: false,
    selectedRowIds: {},
}

export const store = createDevStore(
    persist(
        (set) => ({
            ...DEFAULT_TABLE_SETTINGS,
            setGroupEnabled: (
                groupEnabled: boolean,
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) => {
                set((state: TableSettingsState) => {
                    const newState = {
                        ...state,
                        groupEnabled,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }

                    saveTableViewSettings?.(newState)
                    return newState
                })
            },
            setAvailableColumns: (
                availableColumns: IdTitleProps[],
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) => {
                set((state: TableSettingsState) => {
                    const newState = {
                        ...state,
                        availableColumns,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }
                    saveTableViewSettings?.(newState)
                    return newState
                })
            },
            setSelectedColumns: (
                selectedColumns: IdTitleProps[],
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) => {
                set((state) => {
                    const newOrderBy: string[] = []
                    const orderByArray: string[] = state.orderBy.split(',')
                    selectedColumns.forEach((col) => {
                        const orderCol =
                            orderByArray.find(
                                (orderCol) => orderCol.split(' ')[0] === col.id
                            ) ?? ''
                        if (orderCol) {
                            newOrderBy.push(orderCol)
                        }
                    })

                    const newState = {
                        ...state,
                        selectedColumns,
                        isDataUploaded: false,
                        orderBy: newOrderBy.join(',') ?? '',
                        lastUpdated: new Date().getTime(),
                    }

                    saveTableViewSettings?.(newState)
                    return newState
                })
            },
            setGroupColumns: (
                groupColumns: IdTitleProps[],
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) => {
                set((state) => {
                    const newState = {
                        ...state,
                        groupColumns,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }

                    saveTableViewSettings?.(newState)
                    return newState
                })
            },
            setPinningProps: (
                pinningProps: PinningProps,
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) => {
                set((state) => {
                    const newState = {
                        ...state,
                        pinningProps,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }

                    saveTableViewSettings?.(newState)
                    return newState
                })
            },
            setCollapseColumnChecked: (
                collapseColumnChecked: Record<string, boolean>
            ) => {
                set((state) => {
                    const newState = {
                        ...state,
                        collapseColumnChecked,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }

                    return newState
                })
            },
            setOrderBy: (
                orderBy: string,
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) => {
                set((state) => {
                    const orderByArray: string[] = orderBy.split(',')
                    const newOrderByArray: string[] = []

                    state.selectedColumns.forEach((col) => {
                        const orderCol =
                            orderByArray.find(
                                (column) => column.split(' ')[0] === col.id
                            ) ?? ''

                        if (orderCol) {
                            newOrderByArray.push(orderCol)
                        }
                    })

                    const newOrderByIds = newOrderByArray.map(
                        (col) => col.split(' ')[0]
                    )

                    const groupByIds = state.groupColumns.length
                        ? `${state.groupColumns
                              .map((group) => group.id)
                              .join(',')},`
                        : ''

                    const newOrderByString = newOrderByArray.length
                        ? `${newOrderByArray
                              .filter(
                                  (col) =>
                                      !groupByIds.includes(col.split(' ')[0])
                              )
                              .join(',')},`
                        : ''

                    const sortType = !newOrderByIds.includes('type')
                        ? 'type,'
                        : ''

                    const sortLastUpdated = !newOrderByIds.includes(
                        'lastUpdated'
                    )
                        ? 'lastUpdated desc'
                        : ''

                    const newOrderBy = `${groupByIds}${newOrderByString}${sortType}${sortLastUpdated}`

                    const newState = {
                        ...state,
                        orderBy: newOrderBy,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }
                    saveTableViewSettings?.(newState)
                    return newState
                })
            },
            setFilters: (filters: SpecFilters<ListingMatch>) =>
                set({
                    filters,
                    lastUpdated: new Date().getTime(),
                }),
            setTableSettings: (
                tableSettings: Partial<TableSettingsState>,
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) => {
                set((state) => {
                    const newState = {
                        ...state,
                        ...tableSettings,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }

                    saveTableViewSettings?.(newState)
                    return newState
                })
            },
            setColumnWidths: debounce(
                (
                    columnWidth: Record<string, number>,
                    saveTableViewSettings?: (state: TableSettingsState) => void
                ) => {
                    set((state) => {
                        const newState = {
                            ...state,
                            columnWidths: {
                                ...state.columnWidths,
                                ...columnWidth,
                            },
                            isDataUploaded: false,
                            lastUpdated: new Date().getTime(),
                        }

                        saveTableViewSettings?.(newState)

                        return newState
                    })
                },
                300,
                {
                    trailing: true,
                }
            ),
            setIsTableView: (
                isTableView: boolean,
                saveTableViewSettings?: (state: TableSettingsState) => void
            ) =>
                set((state) => {
                    const newState = {
                        ...state,
                        isTableView,
                        isDataUploaded: false,
                        lastUpdated: new Date().getTime(),
                    }

                    saveTableViewSettings?.(newState)

                    return newState
                }),
            setIsTableSettingLoaded: (isLoaded: boolean) =>
                set({
                    isTableSettingLoaded: isLoaded,
                }),
            setIsDataUploaded: (isUploaded: boolean) =>
                set({
                    isDataUploaded: isUploaded,
                }),
            setSelectedRowIds: (selectedRowIds: Record<string, boolean>) =>
                set({ selectedRowIds }),
        }),
        {
            name: 'table-settings',
            storage: createJSONStorage(() => localStorage),
            skipHydration: true,
        }
    ) as StateCreator<TableSettingsState, [], [['zustand/persist', unknown]]>,
    'TABLE_SETTINGS'
)

export const useTableSettingsStore = create(store)
